import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinPageShopScope, stackShopDimensionTabs } from '@/shein-platform/hooks/useSheinPageShopScope';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, pickField } from '@/shein-platform/utils/sheinResponse';
import { loadProductMetaBySkc } from '@/shein-platform/utils/sheinProductMetaBySkc';
import {
  agencyTypeLabel,
  isBindableAgency,
  isSkcAgencyActionNeeded,
  skcAgencyReviewStateLabel,
  skcAgencyReviewStateModifier,
} from '@/shein-platform/modules/compliance/complianceDisplay';
import ComplianceAutoBindRuleModal from '@/shein-platform/modules/compliance/ComplianceAutoBindRuleModal';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

const BIND_ALL = 'all';
const BIND_UNBOUND = 'unbound';
const BIND_BOUND = 'bound';

/** 维度 Tab：全部 → 未绑定(待绑定/失败) → 已绑定(待审核/成功) */
const BIND_TABS = [
  { id: BIND_ALL, labelKey: 'tiktokCatalog.all' },
  { id: BIND_UNBOUND, labelKey: 'compliance.tabUnbound' },
  { id: BIND_BOUND, labelKey: 'compliance.tabBound' },
];

const TABLE_COLUMNS = [
  { key: 'check', label: '', width: '50px' },
  { key: 'product', labelKey: 'orderList.productInfo', width: '30%' },
  { key: 'skc', label: 'SKC', width: '17.5%' },
  { key: 'agencyType', labelKey: 'compliance.agencyType', width: '17.5%' },
  { key: 'status', labelKey: 'shop.colStatus', width: '17.5%' },
  { key: 'ops', labelKey: 'certLib.colOps', width: '17.5%' },
];

const COL_SPAN = TABLE_COLUMNS.length;

const EMPTY_FILTERS = {
  skcValue: '',
  bindStatus: BIND_UNBOUND,
};

const EMPTY_QUERY = {
  skc: '',
};

/** 同一 SKC 下多代理类型合并为一行展示（代理类型竖列） */
function groupSkcAgencyRows(rows) {
  const groups = [];
  const indexByKey = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const skc = String(pickField(row, 'skc', 'skcName', 'skcCode') || '').trim();
    const groupKey = skc || `row:${index}`;
    if (!indexByKey.has(groupKey)) {
      indexByKey.set(groupKey, groups.length);
      groups.push({
        key: `skc:${groupKey}`,
        skc,
        members: [],
      });
    }
    groups[indexByKey.get(groupKey)].members.push(row);
  });
  return groups.map((group) => {
    const typeMap = new Map();
    group.members.forEach((row) => {
      const type = pickField(row, 'agencyType');
      const typeKey = type == null || type === '' ? '__empty__' : String(type);
      if (!typeMap.has(typeKey)) {
        typeMap.set(typeKey, row);
      }
    });
    const uniqueMembers = Array.from(typeMap.values());
    return {
      ...group,
      uniqueMembers,
      agencyTypeLabels: uniqueMembers.map((row) => agencyTypeLabel(pickField(row, 'agencyType'))),
    };
  });
}

function CellStack({ items, title, itemClassName }) {
  const list = Array.isArray(items) && items.length ? items : ['—'];
  return (
    <div className="spo-cell-stack" title={title || list.join('、')}>
      {list.map((text, idx) => (
        <div key={`${idx}-${text}`} className={itemClassName || 'spo-cell-stack__item'}>
          {text}
        </div>
      ))}
    </div>
  );
}

function extractAgencyDetailList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.info)) return res.info;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.list)) return res.list;
  return extractSheinList(res);
}

function reviewStatusListForFilter(bindFilter) {
  if (bindFilter === BIND_UNBOUND) return [1, 3];
  if (bindFilter === BIND_BOUND) return [2, 4];
  return undefined;
}

function agencyNameOf(row) {
  return pickField(row, 'agencyName', 'companyName', 'name') || i18n.t('compliance.unnamed', { ns: 'workbench' });
}

function agencyIdOf(row) {
  return pickField(row, 'agencyId', 'id', 'companyId');
}

function BindAgencyModal({
  open,
  shopId,
  agencies,
  seedSkc = '',
  seedAgencyType = null,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation('workbench');
  const [skcInput, setSkcInput] = useState('');
  const [agencyType, setAgencyType] = useState('');
  const [agencyKey, setAgencyKey] = useState('');
  const [saving, setSaving] = useState(false);

  const bindableAgencies = useMemo(
    () => (Array.isArray(agencies) ? agencies.filter(isBindableAgency) : []),
    [agencies],
  );

  const typeOptions = useMemo(() => {
    const map = new Map();
    bindableAgencies.forEach((row) => {
      const type = pickField(row, 'agencyType');
      if (type == null || type === '') return;
      const key = String(type);
      if (!map.has(key)) map.set(key, agencyTypeLabel(type));
    });
    // 行上预置的类型即使本地暂无该公司，也展示以便用户知晓需同步
    if (seedAgencyType != null && seedAgencyType !== '') {
      const key = String(seedAgencyType);
      if (!map.has(key)) map.set(key, agencyTypeLabel(seedAgencyType));
    }
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [bindableAgencies, seedAgencyType]);

  const companyOptions = useMemo(() => {
    if (agencyType === '') return [];
    return bindableAgencies.filter(
      (row) => Number(pickField(row, 'agencyType')) === Number(agencyType),
    );
  }, [bindableAgencies, agencyType]);

  useEffect(() => {
    if (!open) return;
    setSkcInput(seedSkc || '');
    setAgencyKey('');
    setAgencyType(
      seedAgencyType != null && seedAgencyType !== ''
        ? String(seedAgencyType)
        : '',
    );
  }, [open, seedSkc, seedAgencyType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!shopId) return;
    if (agencyType === '') {
      showToast(t('compliance.pickAgencyType'), 'error');
      return;
    }
    const selected = companyOptions.find(
      (row) => String(agencyIdOf(row)) === String(agencyKey),
    );
    if (!selected) {
      showToast(t('compliance.pickCompany'), 'error');
      return;
    }
    const agencyId = agencyIdOf(selected);
    const type = pickField(selected, 'agencyType');
    if (type == null || type === '') {
      showToast(t('compliance.missingAgencyType'), 'error');
      return;
    }
    const skcList = String(skcInput || '')
      .split(/[\s,，]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (skcList.length === 0) {
      showToast(t('compliance.missingSkc'), 'error');
      return;
    }
    setSaving(true);
    try {
      await sheinPlatformAPI.saveComplianceSkcAgency(shopId, {
        skc: skcList,
        agencyId,
        agencyType: Number(type),
      });
      showToast(t('compliance.submitted', { count: skcList.length }), 'success');
      onSaved?.();
    } catch (e) {
      showToast(e?.message || t('shop.bindFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-compliance-bind-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-compliance-bind-title">{t('compliance.bindTitle')}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose} disabled={saving}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          <p className="spo-batch-modal__hint">
            {t('compliance.bindHint', { platform: 'SHEIN', place: t('compliance.placeMaterials') })}
          </p>
          <div className="spo-cert-modal__grid">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">SKC <RequiredMark /></span>
              <input
                className="spo-input"
                type="text"
                value={skcInput}
                disabled
                readOnly
                placeholder={t('compliance.skcPh')}
              />
            </label>
            <label className="spo-field">
              <span className="spo-field__label">{t('compliance.agencyType')} <RequiredMark /></span>
              <select
                className="spo-select"
                value={agencyType}
                disabled={saving}
                onChange={(e) => {
                  setAgencyType(e.target.value);
                  setAgencyKey('');
                }}
              >
                <option value="">{t('compliance.pickType')}</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="spo-field">
              <span className="spo-field__label">{t('compliance.agencyCompany')} <RequiredMark /></span>
              <select
                className="spo-select"
                value={agencyKey}
                disabled={saving || !agencyType}
                onChange={(e) => setAgencyKey(e.target.value)}
              >
                <option value="">
                  {!agencyType
                    ? t('compliance.pickTypeFirst')
                    : (companyOptions.length ? t('compliance.pickCompanyPh') : t('compliance.noCompany'))}
                </option>
                {companyOptions.map((row) => {
                  const id = agencyIdOf(row);
                  return (
                    <option key={String(id)} value={String(id)}>
                      {agencyNameOf(row)}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose} disabled={saving}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={submit}
            disabled={saving || !shopId || !skcInput}
          >
            {saving ? t('returnList.submitting') : t('compliance.saveBind')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 产品合规 · 代理绑定：对接 skc-agency-detail（同 SKC 多代理类型合并展示） */
export default function ComplianceDashboard({
  syncKinds = ['skcAgency'],
  active = true,
} = {}) {
  const { t } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const [searchParams] = useSearchParams();
  const bindFromUrl = searchParams.get('bindStatus');
  const initialBind = bindFromUrl === BIND_BOUND || bindFromUrl === BIND_ALL || bindFromUrl === BIND_UNBOUND
    ? bindFromUrl
    : BIND_UNBOUND;

  const [agencies, setAgencies] = useState([]);
  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTERS, bindStatus: initialBind }));
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [queryNonce, setQueryNonce] = useState(0);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [bindFilter, setBindFilter] = useState(initialBind);
  const [skcRows, setSkcRows] = useState([]);
  const [productMeta, setProductMeta] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [unboundCount, setUnboundCount] = useState(0);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [bindSeedSkc, setBindSeedSkc] = useState('');
  const [bindSeedAgencyType, setBindSeedAgencyType] = useState(null);
  const [autoBindModalOpen, setAutoBindModalOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const loadAgencies = useCallback(async ({ silent = false } = {}) => {
    if (!active) return;
    if (!shopId) {
      setAgencies([]);
      return;
    }
    setLoadingAgencies(true);
    try {
      const res = await sheinPlatformAPI.listComplianceAgencies(shopId, {
        pageNum: 1,
        pageSize: 100,
        bindableOnly: true,
      });
      setAgencies(extractSheinList(res));
    } catch (e) {
      setAgencies([]);
      if (!silent) {
        showToast(e?.message || t('compliance.loadAgencyFailed'), 'error');
      }
    } finally {
      setLoadingAgencies(false);
    }
  }, [shopId, active, t]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!active) return { list: [], count: 0 };
    if (!shopId) {
      setSkcRows([]);
      setProductMeta({});
      setTotal(0);
      setUnboundCount(0);
      return { list: [], count: 0 };
    }
    setLoading(true);
    try {
      const skcTokens = parseBatchTokens(query.skc).slice(0, BATCH_QUERY_LIMIT);
      const body = {
        source: 'cached',
        pageNum: page,
        pageSize,
      };
      if (skcTokens.length) {
        body.skcList = skcTokens;
      }
      const reviewStatusList = reviewStatusListForFilter(bindFilter);
      if (reviewStatusList) {
        body.reviewStatusList = reviewStatusList;
      }

      const res = await sheinPlatformAPI.getComplianceSkcDetail(shopId, body);
      const list = extractAgencyDetailList(res);
      setSkcRows(list);

      const totalCount = Number(res?.total ?? res?.count);
      const len = list.length;
      setTotal(Number.isFinite(totalCount) ? totalCount : (len < pageSize ? (page - 1) * pageSize + len : page * pageSize + 1));

      const unboundFromApi = Number(res?.unboundCount);
      if (Number.isFinite(unboundFromApi)) {
        setUnboundCount(unboundFromApi);
      } else if (bindFilter === BIND_UNBOUND) {
        setUnboundCount(Number.isFinite(totalCount) ? totalCount : len);
      }

      const skcs = list.map((row) => pickField(row, 'skc', 'skcName', 'skcCode')).filter(Boolean);
      setProductMeta(await loadProductMetaBySkc(shopId, skcs));
      return { list, count: Number.isFinite(totalCount) ? totalCount : len };
    } catch (e) {
      setSkcRows([]);
      setProductMeta({});
      setTotal(0);
      setUnboundCount(0);
      if (!silent) {
        showToast(e?.message || t('compliance.loadBindFailed'), 'error');
      }
      throw e;
    } finally {
      setLoading(false);
    }
  }, [shopId, bindFilter, page, pageSize, query.skc, queryNonce, active, t]);

  useEffect(() => {
    if (!active) return;
    load().catch(() => {});
  }, [load, active]);

  useEffect(() => {
    if (!active) return;
    loadAgencies({ silent: true });
  }, [loadAgencies, active]);

  useEffect(() => {
    setFilterDraft({ ...EMPTY_FILTERS, bindStatus: initialBind });
    setQuery(EMPTY_QUERY);
    setBatchModalOpen(false);
    setBindFilter(initialBind);
    setPage(1);
    setQueryNonce((n) => n + 1);
    setLastSyncedAt('');
    setSelectedKeys(new Set());
  }, [shopId, initialBind]);

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const tokens = parseBatchTokens(filterDraft.skcValue).slice(0, BATCH_QUERY_LIMIT);
    const nextStatus = filterDraft.bindStatus || BIND_ALL;
    if (shopId) setLoading(true);
    setBindFilter(nextStatus);
    setPage(1);
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({
        skc: tokens.join(','),
      });
      return next;
    });
  };

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setQuery(EMPTY_QUERY);
    setBindFilter(BIND_UNBOUND);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    try {
      await loadAgencies({ silent: true });
      const track = await startJob({
        kind: 'skcAgency',
        shopId,
        label: t('compliance.syncAgencyBind'),
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      setLastSyncedAt(new Date().toISOString());
      await load({ silent: true });
    } catch {
      // startJob / SyncJobContext 已提示失败
    }
  }, [shopId, startJob, onTrackDone, loadAgencies, load, t]);

  const {
    refresh: onSyncCompliance,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('skcAgency', shopId);

  const latestSyncedLabel = lastSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: new Date(lastSyncedAt).toLocaleString('zh-CN', { hour12: false }) })
    : t('tiktokCatalog.notSynced');

  const displayGroups = useMemo(() => groupSkcAgencyRows(skcRows), [skcRows]);

  const selectableKeys = useMemo(
    () => displayGroups.map((group) => group.key),
    [displayGroups],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedRows = useMemo(
    () => displayGroups
      .filter((group) => selectedKeys.has(group.key))
      .flatMap((group) => group.members),
    [displayGroups, selectedKeys],
  );

  const writeComplianceCsv = (exportRows, filename) => {
    if (!exportRows.length) {
      showToast(t('compliance.emptySkc'), 'warning');
      return;
    }
    downloadCsv(
      filename,
      ['skc', 'agencyType', 'agencyId', 'agencyName', 'bindStatus', 'complianceStatus', 'missingRequiredCount'],
      exportRows.map((row) => ({
        skc: pickField(row, 'skc', 'skcName', 'skcCode') || '',
        agencyType: agencyTypeLabel(pickField(row, 'agencyType')),
        agencyId: pickField(row, 'agencyId') || '',
        agencyName: pickField(row, 'agencyName') || '',
        bindStatus: pickField(row, 'bindStatus') || '',
        complianceStatus: pickField(row, 'complianceStatus') || '',
        missingRequiredCount: pickField(row, 'missingRequiredCount') ?? '',
      })),
    );
    showToast(t('compliance.exportedSkc', { count: exportRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const skcTokens = parseBatchTokens(query.skc).slice(0, BATCH_QUERY_LIMIT);
        const body = {
          source: 'cached',
          pageNum: pageNo,
          pageSize: size,
        };
        if (skcTokens.length) {
          body.skcList = skcTokens;
        }
        const reviewStatusList = reviewStatusListForFilter(bindFilter);
        if (reviewStatusList) {
          body.reviewStatusList = reviewStatusList;
        }
        const res = await sheinPlatformAPI.getComplianceSkcDetail(shopId, body);
        const list = extractAgencyDetailList(res);
        return { items: list, total: Number(res?.total ?? res?.count) || 0 };
      });
      writeComplianceCsv(all, `shein-compliance-skc-filtered-${Date.now()}.csv`);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportPage = () => {
    writeComplianceCsv(skcRows, `shein-compliance-skc-page-${Date.now()}.csv`);
  };

  const exportSelected = () => {
    if (!selectedRows.length) {
      showToast(t('compliance.pickExportSkc'), 'warning');
      return;
    }
    writeComplianceCsv(selectedRows, `shein-compliance-skc-selected-${Date.now()}.csv`);
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('compliance.pickDelete'), 'warning');
      return;
    }
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    const skcNames = selectedRows
      .map((row) => pickField(row, 'skc', 'skcName', 'skcCode'))
      .filter((s) => s != null && String(s).trim() !== '')
      .map((s) => String(s).trim());
    if (ids.length === 0 && skcNames.length === 0) {
      showToast(t('compliance.missingRowId'), 'error');
      return;
    }
    const countHint = ids.length || skcNames.length;
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('compliance.deleteMsg', { count: countHint, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteComplianceSkcAgency(shopId, {
        ids,
        skcNames: ids.length ? [] : skcNames,
      });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : countHint;
      if (count > 0) {
        showToast(t('compliance.deleted', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      await load();
    } catch (e) {
      showToast(e?.message || t('shop.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const statusTabs = (
    <div className="spo-status-tabs" role="tablist" aria-label={t('compliance.bindAria')}>
      {BIND_TABS.map((tab) => {
        const active = bindFilter === tab.id;
        const count = tab.id === BIND_UNBOUND ? unboundCount : null;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
            onClick={() => {
              setBindFilter(tab.id);
              setFilterDraft((prev) => ({ ...prev, bindStatus: tab.id }));
              setPage(1);
            }}
          >
            {t(tab.labelKey)}
            {count != null && count > 0 ? (
              <span className="spo-status-tab__count">{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <SheinSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || loadingAgencies || deleting || exporting}
            syncKinds={syncKinds}
            quotaKind="skc-agency"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              onSyncCompliance();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('compliance.syncHint', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: skcRows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            extraActions={[
              {
                key: 'auto-rules',
                label: t('compliance.autoRule'),
                disabled: !shopId || syncing || loading || loadingAgencies || deleting,
                onClick: () => setAutoBindModalOpen(true),
              },
              {
                key: 'bind',
                label: t('compliance.bindAgency'),
                tone: 'primary',
                disabled: !shopId,
                onClick: () => {
                  const selectedSkcs = selectedRows
                    .map((row) => pickField(row, 'skc', 'skcName', 'skcCode'))
                    .filter((s) => s != null && String(s).trim() !== '')
                    .map((s) => String(s).trim());
                  const uniqueSkcs = [...new Set(selectedSkcs)];
                  const seedType = selectedRows.length === 1
                    ? (selectedRows[0]?.agencyType ?? null)
                    : null;
                  setBindSeedSkc(uniqueSkcs.join(','));
                  setBindSeedAgencyType(seedType);
                  setBindOpen(true);
                  if (agencies.length === 0) {
                    loadAgencies({ silent: false });
                  }
                },
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch">
              <select
                className="spo-select spo-select--compact"
                value="skc"
                disabled={!shopId}
                aria-label={t('compliance.idType')}
              >
                <option value="skc">SKC</option>
              </select>
              <input
                className="spo-input"
                value={filterDraft.skcValue}
                placeholder={t('publishRecord.idPlaceholder', { type: 'SKC' })}
                disabled={!shopId}
                onChange={(e) => updateField('skcValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: 'SKC' })}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="spo-field spo-field--inline spo-field--cert-type">
              <span className="spo-field__label">{t('compliance.statusQuery')}</span>
              <select
                className="spo-select spo-select--cert-type"
                value={filterDraft.bindStatus}
                disabled={!shopId}
                onChange={(e) => updateField('bindStatus', e.target.value)}
                aria-label={t('compliance.statusQuery')}
              >
                {BIND_TABS.map((tab) => (
                  <option key={tab.id} value={tab.id}>{t(tab.labelKey)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onReset} disabled={!shopId}>
              {t('returnList.reset')}
            </button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <BatchQueryModal
        open={batchModalOpen}
        title={t('compliance.batchSkcTitle')}
        initialValue={filterDraft.skcValue}
        placeholder={t('orderList.idLinePh', { type: 'SKC' })}
        onConfirm={(tokens) => {
          updateField('skcValue', joinBatchTokens(tokens));
        }}
        onClose={() => setBatchModalOpen(false)}
      />

      <ProductListCard tabs={stackShopDimensionTabs(shopDimensionTabs, statusTabs)} loading={Boolean(shopId) && loading} loadingLabel={t('compliance.querying')}>
        <div className="spo-drafts__table-wrap spo-certs-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-certs-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={`spo-certs-col spo-certs-col--${col.key}`}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-certs-th spo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('compliance.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`spo-certs-th spo-certs-th--${col.key}`}
                  >
                    {col.labelKey ? t(col.labelKey) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || displayGroups.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">
                        {!shopId
                          ? (allShopsScope ? t('draftBox.pickConcrete') : t('returnList.pickShop'))
                          : loading
                            ? t('returnList.loading')
                            : bindFilter === BIND_UNBOUND
                              ? t('compliance.emptyUnbound')
                              : t('compliance.emptyBind')}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="spo-certs-empty__desc">
                          {bindFilter === BIND_UNBOUND
                            ? t('compliance.emptySwitch', { platform: 'SHEIN' })
                            : t('compliance.emptyRefresh', { platform: 'SHEIN' })}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                displayGroups.map((group) => {
                  const key = group.key;
                  const skc = group.skc;
                  const uniqueMembers = group.uniqueMembers;
                  const needsBind = uniqueMembers.some((row) => (
                    isSkcAgencyActionNeeded(pickField(row, 'reviewState', 'reviewStatus'))
                  ));
                  const seedRow = uniqueMembers.find((row) => (
                    isSkcAgencyActionNeeded(pickField(row, 'reviewState', 'reviewStatus'))
                  )) || uniqueMembers[0];
                  const seedAgencyType = seedRow ? pickField(seedRow, 'agencyType') : null;
                  const meta = productMeta[String(skc || '').trim()] || {};
                  return (
                    <tr key={key}>
                      <td
                        className="spo-certs-td spo-certs-td--check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('orderList.selectRow', { title: skc || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="spo-certs-td spo-certs-td--product">
                        <div className="spo-photo-product">
                          {meta.imageUrl ? (
                            <SheinProductThumb
                              imageUrl={meta.imageUrl}
                              className="spo-photo-product__img"
                              placeholderClassName="spo-photo-product__img spo-photo-product__img--empty"
                              alt={meta.title || skc || ''}
                            />
                          ) : (
                            <div className="spo-photo-product__img spo-photo-product__img--empty" />
                          )}
                          <div className="spo-photo-product__meta">
                            <HoverCopyField
                              value={meta.title || ''}
                              className="spo-photo-product__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label="SPU"
                              value={meta.spuName}
                              className="spo-photo-product__skc"
                              separator=" "
                            />
                          </div>
                        </div>
                      </td>
                      <td className="spo-certs-td">
                        <HoverCopyField
                          label="SKC"
                          value={skc}
                          className="spo-photo-product__skc"
                          separator=" "
                        />
                      </td>
                      <td className="spo-certs-td spo-certs-td--wrap">
                        <CellStack items={group.agencyTypeLabels} />
                      </td>
                      <td className="spo-certs-td spo-certs-td--wrap">
                        <div className="spo-cell-stack">
                          {uniqueMembers.map((row, idx) => {
                            const reviewState = pickField(row, 'reviewState', 'reviewStatus');
                            const type = pickField(row, 'agencyType');
                            return (
                              <div
                                key={`${type ?? 'x'}-${idx}`}
                                className="spo-cell-stack__item"
                              >
                                <span className={`spo-records__status spo-records__status--${skcAgencyReviewStateModifier(reviewState)}`}>
                                  {skcAgencyReviewStateLabel(reviewState)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="spo-certs-td spo-certs-td--ops">
                        <div className="spo-drafts__actions">
                          <button
                            type="button"
                            className="spo-btn spo-btn--primary spo-btn--sm"
                            onClick={() => {
                              setBindSeedSkc(skc || '');
                              setBindSeedAgencyType(seedAgencyType ?? null);
                              setBindOpen(true);
                            }}
                          >
                            {needsBind ? t('compliance.bind') : t('compliance.rebind')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('compliance.pager')}
        />
      </ProductListCard>

      <BindAgencyModal
        open={bindOpen}
        shopId={shopId}
        agencies={agencies}
        seedSkc={bindSeedSkc}
        seedAgencyType={bindSeedAgencyType}
        onClose={() => setBindOpen(false)}
        onSaved={() => {
          setBindOpen(false);
          load().catch(() => {});
          loadAgencies();
        }}
      />

      <ComplianceAutoBindRuleModal
        open={autoBindModalOpen}
        shopId={shopId}
        kind="agency"
        busy={syncing || loading || loadingAgencies || deleting}
        onClose={() => setAutoBindModalOpen(false)}
        onRan={() => {
          load().catch(() => {});
          loadAgencies({ silent: true });
        }}
      />
    </>
  );
}
